// ─────────────────────────────────────────────
//  Mobile design tokens — native (Capacitor) skin only.
//  Sibling namespace over src/theme/tokens.js (DESIGN-SPEC §1, I-1):
//  the base module is imported and SPREAD, never edited or mutated.
//  Web code paths keep importing ./tokens; only the native ThemeProvider
//  publishes this object.
//
//  Client iteration (2026-08, post-spec — overrides spec §1.3 where they
//  conflict): the vivid Canva blue was rejected TWICE as "too bright".
//  Final approved room-card surface is a muted navy gradient
//  #0e2b5e → #0a1f47 with border #22447f, provider gold #d9b44a
//  (muted — NOT #ffd34d), and chip surface #b9c9e2 with ink #0e2b5e.
//  Break-state colors are the client set: giving amber #f59e0b,
//  done green #22c55e, requested red #ef4444.
//
//  Values are plain numbers for space/radius (same trap as tokens.js —
//  spec §0.3): React inline styles append "px", but template-literal CSS
//  silently drops the declaration. Wrap with px() in template literals.
//
// WCAG 2.x contrast — computed with the relative-luminance formula
// (mirrored 1:1 by tokens.mobile.test.js via the exported contrastAudit).
// Thresholds: normal text ≥ 4.5, large text (≥18.66px bold / ≥24px) ≥ 3.0,
// non-text indicators ≥ 3.0.
//
// Pair                                              Ratio   Threshold  Pass
// text.primary #FFFFFF / surface.canvas #02306E     12.72   4.5        ✓
// text.primary #FFFFFF / surface.cardTop #0e2b5e    13.76   4.5        ✓
// text.primary #FFFFFF / surface.cardBottom #0a1f47 16.16   4.5        ✓
// text.primary #FFFFFF / surface.sunken #021E45     16.48   4.5        ✓
// text.primary #FFFFFF / surface.ink #0A1A3A        17.19   4.5        ✓
// text.primary #FFFFFF / surface.grid #031936       17.56   4.5        ✓
// text.muted #A9BEDC   / surface.canvas             6.72    4.5        ✓
// text.muted #A9BEDC   / surface.cardTop            7.27    4.5        ✓
// text.muted #A9BEDC   / surface.cardBottom         8.53    4.5        ✓
// text.muted #A9BEDC   / surface.ink                9.07    4.5        ✓
// text.muted #A9BEDC   / surface.grid               9.27    4.5        ✓
// text.link #8FC0FF    / surface.canvas             6.76    4.5        ✓
// text.link #8FC0FF    / surface.cardTop            7.31    4.5        ✓
// text.link #8FC0FF    / surface.cardBottom         8.58    4.5        ✓
// accent.gold #d9b44a  / surface.canvas             6.41    4.5        ✓
// accent.gold #d9b44a  / surface.cardTop            6.93    4.5        ✓
// accent.gold #d9b44a  / surface.cardBottom         8.14    4.5        ✓
// accent.chipInk #0e2b5e / accent.chipSurface #b9c9e2 8.20  4.5        ✓
// text.primary #FFFFFF / accent.primary #005CD6     6.00    4.5        ✓
// break.giving #f59e0b / surface.grid               8.17    4.5        ✓
// break.done #22c55e   / surface.grid               7.70    4.5        ✓
// break.requested #ef4444 / surface.grid            4.67    4.5        ✓
// break.requested #ef4444 / surface.cardTop         3.66    3.0 LARGE  ✓
// break.requested #ef4444 / surface.cardBottom      4.29    3.0 LARGE  ✓
// break.glyphInk #081226 / break.giving             8.69    4.5        ✓
// break.glyphInk #081226 / break.done               8.19    4.5        ✓
// break.glyphInk #081226 / break.requested          4.96    4.5        ✓
// focus.ring #7FE7FF   / surface.canvas             8.95    3.0 UI     ✓
//
// Adjustments made to pass (mechanism, so it is not re-litigated):
// 1. break.requested #ef4444 as TEXT passes normal-size AA only on
//    surface.grid (4.67). On the card gradient it is 3.66–4.29, so on
//    cards it is restricted to large text (≥18.66px bold) or pill FILLS
//    with an ink label — never 14px body copy.
// 2. Glyph ink on break fills: the spec's text.onLight #0D2A52 measures
//    only 3.80 on the client red #ef4444 (fail). break.glyphInk was
//    darkened to #081226 → 4.96 on red, 8.19–8.69 on green/amber.
//    Client-approved fills are untouched; only the glyph ink moved.
// 3. border.card #22447f is the CLIENT-APPROVED decorative card edge
//    (1.33 vs canvas — decorative, exempt). Where a perceivable boundary
//    is required (empty break cells, focus, outlines), use
//    border.default #6E8FC0 (3.85 non-text on canvas ✓), per spec §1.3.
// ─────────────────────────────────────────────

import base from "./tokens";

// Template-literal helper (spec §0.3): `padding: ${px(space.md)};`
export const px = (n) => `${n}px`;

// ⚠️ MINIMUM FONT SIZE FOR ANY FOCUSABLE TEXT FIELD ON iOS.
//
// WKWebView AUTO-ZOOMS when a text input smaller than 16px receives focus, and
// once zoomed the whole page pans side to side — the user reported the app
// "moving side to side" after tapping a search field. Nothing was overflowing:
// every screen measured exactly 390px wide. The zoom was the movement.
//
// The other fix is `maximum-scale=1` on the viewport meta, which stops the zoom
// by forbidding ALL zooming — including the pinch-zoom a user with low vision
// depends on. Making the field 16px costs two pixels of type and takes nothing
// away, so the base scale's 14px `body` is deliberately NOT used on inputs.
export const INPUT_FONT_SIZE = 16;

const color = {
  surface: {
    canvas: "#02306E", // unified page ground for every mobile screen (spec §1.3)
    cardTop: "#0e2b5e", // client-final room-card gradient start ("too bright" x2 fix)
    cardBottom: "#0a1f47", // client-final room-card gradient end
    cardGradient: "linear-gradient(180deg, #0e2b5e 0%, #0a1f47 100%)",
    sunken: "#021E45", // scroll wells, message list, board backdrop
    ink: "#0A1A3A", // highest-emphasis panel (room status, modals, sheets)
    grid: "#031936", // break-board table interior
    overlay: "rgba(2,20,40,0.72)", // modal / sheet scrim
  },
  border: {
    card: "#22447f", // client-approved card edge — decorative (see header note 3)
    default: "#6E8FC0", // accessible outline: 3.85 on canvas, 4.59 on card
    subtle: "rgba(110,143,192,0.35)", // in-card row dividers (decorative)
    grid: "#7797B4", // break-board rules — 5.74 on grid
  },
  text: {
    primary: "#FFFFFF",
    muted: "#A9BEDC",
    link: "#8FC0FF",
    onLight: "#0D2A52", // text inside white/chip pills and panels
  },
  accent: {
    primary: "#005CD6", // primary CTA fill — white on it 6.00
    primaryPressed: "#0049AC",
    gold: "#d9b44a", // client-final muted provider gold (NOT #ffd34d)
    cyan: "#45BBFF", // informational accent, section eyebrows
    chipSurface: "#b9c9e2", // client-final chip fill
    chipInk: "#0e2b5e", // label on chipSurface — 8.20
  },
  focus: {
    ring: "#7FE7FF", // 8.95 on canvas
  },
  break: {
    giving: "#f59e0b", // in-progress / a reliever is covering
    done: "#22c55e", // meal locked done
    requested: "#ef4444", // waiting for cover — flashing state
    glyphInk: "#081226", // ✓ ◐ ! glyph ink on the three fills (header note 2)
    emptyCell: "#12345E", // + 1.5px border.default so the cell is perceivable
  },
};

// Machine-readable form of the header table. The unit test recomputes every
// ratio with a real relative-luminance implementation and asserts both the
// threshold and that the documented number is honest.
export const contrastAudit = [
  { label: "text.primary / surface.canvas", fg: color.text.primary, bg: color.surface.canvas, ratio: 12.72, threshold: 4.5 },
  { label: "text.primary / surface.cardTop", fg: color.text.primary, bg: color.surface.cardTop, ratio: 13.76, threshold: 4.5 },
  { label: "text.primary / surface.cardBottom", fg: color.text.primary, bg: color.surface.cardBottom, ratio: 16.16, threshold: 4.5 },
  { label: "text.primary / surface.sunken", fg: color.text.primary, bg: color.surface.sunken, ratio: 16.48, threshold: 4.5 },
  { label: "text.primary / surface.ink", fg: color.text.primary, bg: color.surface.ink, ratio: 17.19, threshold: 4.5 },
  { label: "text.primary / surface.grid", fg: color.text.primary, bg: color.surface.grid, ratio: 17.56, threshold: 4.5 },
  { label: "text.muted / surface.canvas", fg: color.text.muted, bg: color.surface.canvas, ratio: 6.72, threshold: 4.5 },
  { label: "text.muted / surface.cardTop", fg: color.text.muted, bg: color.surface.cardTop, ratio: 7.27, threshold: 4.5 },
  { label: "text.muted / surface.cardBottom", fg: color.text.muted, bg: color.surface.cardBottom, ratio: 8.53, threshold: 4.5 },
  { label: "text.muted / surface.ink", fg: color.text.muted, bg: color.surface.ink, ratio: 9.07, threshold: 4.5 },
  { label: "text.muted / surface.grid", fg: color.text.muted, bg: color.surface.grid, ratio: 9.27, threshold: 4.5 },
  { label: "text.link / surface.canvas", fg: color.text.link, bg: color.surface.canvas, ratio: 6.76, threshold: 4.5 },
  { label: "text.link / surface.cardTop", fg: color.text.link, bg: color.surface.cardTop, ratio: 7.31, threshold: 4.5 },
  { label: "text.link / surface.cardBottom", fg: color.text.link, bg: color.surface.cardBottom, ratio: 8.58, threshold: 4.5 },
  { label: "accent.gold / surface.canvas", fg: color.accent.gold, bg: color.surface.canvas, ratio: 6.41, threshold: 4.5 },
  { label: "accent.gold / surface.cardTop", fg: color.accent.gold, bg: color.surface.cardTop, ratio: 6.93, threshold: 4.5 },
  { label: "accent.gold / surface.cardBottom", fg: color.accent.gold, bg: color.surface.cardBottom, ratio: 8.14, threshold: 4.5 },
  { label: "accent.chipInk / accent.chipSurface", fg: color.accent.chipInk, bg: color.accent.chipSurface, ratio: 8.2, threshold: 4.5 },
  { label: "text.primary / accent.primary", fg: color.text.primary, bg: color.accent.primary, ratio: 6.0, threshold: 4.5 },
  { label: "break.giving / surface.grid", fg: color.break.giving, bg: color.surface.grid, ratio: 8.17, threshold: 4.5 },
  { label: "break.done / surface.grid", fg: color.break.done, bg: color.surface.grid, ratio: 7.7, threshold: 4.5 },
  { label: "break.requested / surface.grid", fg: color.break.requested, bg: color.surface.grid, ratio: 4.67, threshold: 4.5 },
  { label: "break.requested / surface.cardTop (LARGE only)", fg: color.break.requested, bg: color.surface.cardTop, ratio: 3.66, threshold: 3.0 },
  { label: "break.requested / surface.cardBottom (LARGE only)", fg: color.break.requested, bg: color.surface.cardBottom, ratio: 4.29, threshold: 3.0 },
  { label: "break.glyphInk / break.giving", fg: color.break.glyphInk, bg: color.break.giving, ratio: 8.69, threshold: 4.5 },
  { label: "break.glyphInk / break.done", fg: color.break.glyphInk, bg: color.break.done, ratio: 8.19, threshold: 4.5 },
  { label: "break.glyphInk / break.requested", fg: color.break.glyphInk, bg: color.break.requested, ratio: 4.96, threshold: 4.5 },
  { label: "focus.ring / surface.canvas (non-text)", fg: color.focus.ring, bg: color.surface.canvas, ratio: 8.95, threshold: 3.0 },
];

const mobileTokens = {
  // Reused from the base scale UNCHANGED (spec §1.2) — spread copies, so the
  // web token object is never shared or mutated (I-1).
  space: { ...base.space },
  radius: { ...base.radius },
  font: {
    family: base.font.family, // decision: keep IBM Plex Sans (spec §1.2)
  },
  // Mobile-only dark palette — sibling namespace, nothing overwrites a base key.
  color,
  layout: {
    // The shell is a PHONE layout, and isNative() is true on a tablet too — an
    // Android tablet or an iPad runs this same tree. Measured on a 1280dp
    // tablet, the cards hugged the left edge with ~60% of the screen empty
    // beside them, which reads as a broken app rather than a roomy one.
    // Capping the content column and centring it keeps a phone identical
    // (every phone is narrower than this) and makes a tablet deliberate.
    // 720 ≈ the widest a single column of clinical cards stays scannable;
    // past that the eye has to travel to pair a name with its room.
    contentMaxWidth: 720,
  },
};

export default mobileTokens;
